import api from "./api";
import { Applicant } from "@/types";

/**
 * Service layer for Applicant (Recruitment) API endpoints.
 * Base path: /api/v1/applicants
 */
export const applicantService = {
  /**
   * Submit a new job application with CV.
   * Content-Type is set to multipart/form-data.
   *
   * @param formData - FormData containing name, email, phone, jobPosting, cv file
   * @returns The created Applicant record
   */
  /**
   * Submit a new job application with CV.
   * Content-Type is set to multipart/form-data.
   *
   * @param formData - FormData containing applicantName, email, phone, jobId, cv file, message
   * @returns The created Applicant record
   */
  submitApplication: async (formData: FormData): Promise<Applicant> => {
    const data: any = await api.post("/applicants", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 600000,
    });
    return data?.doc || data;
  },

  /**
   * Fetch all job applications (Admin only).
   * @param params - Query parameters for filtering and pagination
   * @returns Array of Applicant items or Paginated Response
   */
  getApplicants: async (params?: {
    page?: number;
    limit?: number;
    jobId?: string;
    status?: string;
    search?: string;
  }): Promise<{ docs: Applicant[]; totalPages: number; currentPage: number } | Applicant[]> => {
    const response: any = await api.get("/applicants", { params });

    // The API structure provided by user: data: [...], meta: {...}
    const data = response?.data || response;

    if (Array.isArray(data)) {
      const docs = data.map((item: any) => ({
        ...item,
        id: item._id || item.id,
      }));

      // If there's meta information, return a paginated object
      if (response?.meta) {
        return {
          docs,
          totalPages: response.meta.totalPage || 1,
          currentPage: response.meta.page || 1,
        };
      }

      return docs;
    }

    return [];
  },

  /**
   * Update the status of an application.
   * @param id - The applicant ID
   * @param status - The new status (e.g., 'reviewed', 'rejected', 'accepted')
   */
  /**
   * Update the status of an application.
   * @param id - The applicant ID
   * @param status - The new status (e.g., 'Reviewing', 'Accepted', 'Rejected')
   * @param note - Optional note for the status change
   */
  updateApplicantStatus: async (
    id: string,
    status: string,
    note?: string,
  ): Promise<Applicant> => {
    const data: any = await api.patch(`/applicants/${id}/status`, {
      status,
      note,
    });
    return {
      ...(data?.doc || data),
      id: (data?.doc || data)._id || (data?.doc || data).id,
    };
  },

  /**
   * Delete an application.
   * @param id - The applicant ID
   */
  deleteApplicant: async (id: string): Promise<void> => {
    await api.delete(`/applicants/${id}`);
  },
};
